import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEyeSlash, faExpandArrowsAlt } from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import Question from '../../components/Question';

// Import rune images
import algizImage from '../../../img/runes/image-014.webp';
import tiwazImage from '../../../img/runes/image-016.webp';
import ansuzImage from '../../../img/runes/image-003.webp';

const RunesEthicsLesson = ({ 
  level = 'beginner', 
  onNextLesson, 
  markLessonComplete,
  currentQuestion = 0,
  onQuestionChange,
  currentLesson,
  completedQuestions = [],
  setCompletedQuestions
}) => {
  const sections = [
    {
      id: 'responsibility',
      title: 'I. The Responsibility of the Rune Reader',
      content: `
        In ancient Germanic society, those who worked with runes held positions of great responsibility and respect. 
        The rune reader was seen as a bridge between the human and divine realms, someone who could access hidden knowledge and wisdom.
        
        This responsibility extended beyond mere divination to include ethical considerations about how this knowledge should be used. 
        The rune reader was expected to act with integrity, honesty, and respect for both the querent and the runes themselves.
        
        Historical evidence suggests that rune readers were often consulted for important decisions affecting the community, 
        indicating the trust placed in their abilities and ethical judgment.
      `,
      imageUrl: algizImage,
      questions: {
        beginner: {
          question: "What was the rune reader considered in ancient Germanic society?",
          options: ["A bridge between human and divine realms", "A simple fortune teller", "A religious leader", "A warrior"],
          correctAnswer: "A bridge between human and divine realms",
        },
        intermediate: {
          question: "What qualities were expected of a rune reader?",
          options: [
            "Integrity, honesty, and respect",
            "Only technical skill",
            "Religious devotion",
            "Political influence"
          ],
          correctAnswer: "Integrity, honesty, and respect",
        },
      },
    },
    {
      id: 'intent',
      title: 'II. Intent and Purpose in Runic Work',
      content: `
        The intent behind runic divination was crucial in ancient practice. Runes were not used for trivial matters or entertainment, 
        but for serious questions requiring wisdom and guidance.
        
        The purpose of runic consultation was to gain insight, not to predict the future in absolute terms. 
        The runes were seen as revealing possibilities and patterns rather than fixed outcomes, 
        emphasizing the role of personal choice and responsibility in shaping one's destiny.
        
        This understanding reflects the Germanic concept of wyrd (fate), which was not seen as completely predetermined, 
        but as a web of possibilities that could be influenced by one's actions and choices.
      `,
      imageUrl: tiwazImage,
      questions: {
        beginner: {
          question: "What was the primary purpose of runic consultation?",
          options: ["Entertainment", "Gaining insight and guidance", "Predicting exact future events", "Religious ritual"],
          correctAnswer: "Gaining insight and guidance",
        },
        intermediate: {
          question: "How were runes viewed in relation to the future?",
          options: [
            "As revealing fixed outcomes",
            "As revealing possibilities and patterns",
            "As predicting exact dates",
            "As showing past events only"
          ],
          correctAnswer: "As revealing possibilities and patterns",
        },
      },
    },
    {
      id: 'respect',
      title: 'III. Respect for the Tradition',
      content: `
        Respect for the runic tradition involves understanding its cultural and historical context. 
        The runes are not just symbols but carry the weight of centuries of Germanic cultural heritage.
        
        This respect extends to how we approach the runes - with humility, patience, and a willingness to learn. 
        The ancient rune readers spent years studying and practicing before being considered proficient.
        
        Respect also means acknowledging the limitations of our knowledge. While we can study historical sources and archaeological evidence, 
        we must recognize that much of the original runic practice has been lost to time, and we should be cautious about making definitive claims.
      `,
      imageUrl: ansuzImage,
      questions: {
        beginner: {
          question: "How should we approach the runic tradition?",
          options: ["With arrogance", "With humility and patience", "With skepticism", "With religious fervor"],
          correctAnswer: "With humility and patience",
        },
        intermediate: {
          question: "What should we acknowledge about our knowledge of runic practice?",
          options: [
            "That we know everything",
            "That much has been lost to time",
            "That it's all modern invention",
            "That it's completely preserved"
          ],
          correctAnswer: "That much has been lost to time",
        },
      },
    },
    {
      id: 'modern',
      title: 'IV. Modern Ethical Considerations',
      content: `
        In modern practice, ethical considerations include being honest about the nature of runic divination. 
        It's important to explain that runes offer guidance and insight rather than absolute predictions.
        
        The reader should respect the querent's autonomy and not attempt to make decisions for them. 
        The role is to provide information and perspective, allowing the querent to make their own informed choices.
        
        Confidentiality is also important - what is shared in a runic consultation should remain private unless there's a clear danger to someone's safety.
        This respect for privacy builds trust and creates a safe space for honest exploration of questions and concerns.
      `,
      imageUrl: algizImage,
      questions: {
        beginner: {
          question: "What should a modern rune reader be honest about?",
          options: ["Making absolute predictions", "The nature of runic divination", "Having supernatural powers", "Being infallible"],
          correctAnswer: "The nature of runic divination",
        },
        intermediate: {
          question: "What is the reader's role in helping the querent?",
          options: [
            "Making decisions for them",
            "Providing information and perspective",
            "Telling them what to do",
            "Predicting their future"
          ],
          correctAnswer: "Providing information and perspective",
        },
      },
    },
    {
      id: 'boundaries',
      title: 'V. Setting Appropriate Boundaries',
      content: `
        Setting appropriate boundaries is essential for ethical runic practice. This includes being clear about what runic divination can and cannot do.
        
        Runes should not be used to replace professional medical, legal, or psychological advice. 
        If someone needs help with serious issues like health problems, legal matters, or mental health concerns, 
        they should be directed to appropriate professionals.
        
        The reader should also set boundaries around their own time and energy. 
        It's important to recognize when one is not in the right state of mind to perform a reading, 
        and to be honest about one's level of experience and expertise.
      `,
      imageUrl: tiwazImage,
      questions: {
        beginner: {
          question: "What should runes NOT replace?",
          options: ["Professional medical, legal, or psychological advice", "Personal decisions", "Religious practices", "Cultural traditions"],
          correctAnswer: "Professional medical, legal, or psychological advice",
        },
        intermediate: {
          question: "When should a reader decline to perform a reading?",
          options: [
            "When not in the right state of mind",
            "When the querent is nervous",
            "When it's their first time",
            "When the question is difficult"
          ],
          correctAnswer: "When not in the right state of mind",
        },
      },
    },
  ];

  const [selectedOption, setSelectedOption] = useState('');
  const [imageVisible, setImageVisible] = useState(true);
  const [imageSize, setImageSize] = useState('smallimage');
  const [allQuestionsAnsweredCorrectly, setAllQuestionsAnsweredCorrectly] = useState(false);

  useEffect(() => {
    if (allQuestionsAnsweredCorrectly) {
      markLessonComplete();
      onNextLesson();
    }
  }, [allQuestionsAnsweredCorrectly, markLessonComplete, onNextLesson]);

  const handleAnswer = (isCorrect) => {
    if (isCorrect) {
      const newCompletedQuestions = [...completedQuestions];
      newCompletedQuestions[currentQuestion] = true;
      setCompletedQuestions(newCompletedQuestions);
      
      if (currentQuestion + 1 < sections.length) {
        onQuestionChange(currentQuestion + 1);
      } else {
        setAllQuestionsAnsweredCorrectly(true);
      }
    }
  };

  const toggleImageVisibility = () => {
    setImageVisible(!imageVisible);
  };

  const toggleImageSize = () => {
    setImageSize(imageSize === 'smallimage' ? 'largeimage' : 'smallimage');
  };

  const getImageStyle = () => {
    const baseStyle = {
      height: 'auto',
      borderRadius: '8px',
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
      transition: 'all 0.3s ease-in-out'
    };

    if (imageSize === 'smallimage') {
      return {
        ...baseStyle,
        maxWidth: '150px',
        width: '150px'
      };
    } else {
      return {
        ...baseStyle,
        maxWidth: '300px',
        width: '300px'
      };
    }
  };

  const transition = { duration: 0.15, ease: "easeInOut" };

  return (
    <div>
      <Helmet>
        <title>Ethics and Responsibilities - {level.charAt(0).toUpperCase() + level.slice(1)} Lesson</title>
      </Helmet>

      <AnimatePresence mode="wait">
        <motion.div
          key={currentQuestion}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={transition}
        >
          {imageVisible && sections[currentQuestion]?.imageUrl && (
            <div className="has-text-centered mb-4">
              <img
                src={sections[currentQuestion].imageUrl}
                alt={`Runic Ethics - ${sections[currentQuestion].title}`}
                style={getImageStyle()}
              />
              <div className="icon-buttons mt-2">
                <FontAwesomeIcon
                  icon={faEyeSlash}
                  className="icon-button has-text-warning mr-3"
                  onClick={toggleImageVisibility}
                  style={{ cursor: 'pointer' }}
                />
                <FontAwesomeIcon
                  icon={faExpandArrowsAlt}
                  className="icon-button has-text-warning"
                  onClick={toggleImageSize}
                  style={{ cursor: 'pointer' }}
                />
              </div>
            </div>
          )}

          <section className="section has-text-centered" id={sections[currentQuestion]?.id}>
            <div className="container content">
              <div className="columns">
                <div className="column">
                  <h2 className="has-text-primary">{sections[currentQuestion]?.title}</h2>
                  <p className="has-text-white">{sections[currentQuestion]?.content}</p>
                </div>
              </div>
            </div>
          </section>

          {sections[currentQuestion]?.questions && (
            <section className="section has-text-centered">
              <div className="container content">
                <div className="columns">
                  <div className="column">
                    <Question
                      question={sections[currentQuestion].questions[level].question}
                      options={sections[currentQuestion].questions[level].options}
                      correctAnswer={sections[currentQuestion].questions[level].correctAnswer}
                      onAnswered={handleAnswer}
                    />
                  </div>
                </div>
              </div>
            </section>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default RunesEthicsLesson; 